import React, { useState } from "react";
import { X, Mail, Loader2 } from "lucide-react";
import { Button } from "./ui/Button";
import { Input } from "./ui/input2";
import { Portal } from "./ui/Portal";
import { sendBusinessVerificationOtp, verifyBusinessClaimOtp } from "../services/companyOtp";

interface VerifyAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyEmail: string;
  onVerified: () => void;
}

export default function VerifyAccountModal({
  isOpen,
  onClose,
  companyEmail,
  onVerified,
}: VerifyAccountModalProps) {
  const [step, setStep] = useState<"send" | "verify">("send");
  const [otp, setOtp] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSendOtp = async (isResend = false) => {
    setError(null);
    if (isResend) setResendLoading(true);
    else setSendLoading(true);
    try {
      await sendBusinessVerificationOtp();
      setStep("verify");
      setSuccessMessage(isResend ? "OTP sent again to your email." : "OTP sent to your business email!");
      setOtp("");
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to send OTP.");
    } finally {
      if (isResend) setResendLoading(false);
      else setSendLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!otp.trim()) {
      setError("Please enter the OTP.");
      return;
    }
    setError(null);
    setVerifyLoading(true);
    try {
      await verifyBusinessClaimOtp(otp.trim());
      setSuccessMessage("Business verified and claimed successfully!");
      onVerified();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Invalid or expired OTP.");
    } finally {
      setVerifyLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div
          className="relative bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Verify Account</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              To verify your business account, we will send a one-time password (OTP) to your business email.
            </p>
            <div className="flex items-center gap-2 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3">
              <Mail className="w-5 h-5 text-[#885ABB] shrink-0" />
              <span className="text-sm font-medium text-gray-800 break-all">{companyEmail || "Your business email"}</span>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {successMessage && (
              <div className="rounded-lg bg-green-50 border border-green-100 px-4 py-2 text-sm text-green-700">
                {successMessage}
              </div>
            )}

            {step === "send" ? (
              <Button
                type="button"
                disabled={sendLoading}
                onClick={() => handleSendOtp()}
                className="w-full h-11 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-xl font-semibold"
              >
                {sendLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  "Send OTP"
                )}
              </Button>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enter OTP</label>
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                    placeholder="6-character code"
                    className="h-11 text-center text-lg tracking-widest"
                    maxLength={6}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={resendLoading}
                    onClick={() => handleSendOtp(true)}
                    className="flex-1 h-11 border-gray-300 text-gray-700 rounded-xl"
                  >
                    {resendLoading ? <Loader2 size={18} className="animate-spin" /> : "Resend OTP"}
                  </Button>
                  <Button
                    type="button"
                    disabled={verifyLoading || otp.length < 6}
                    onClick={handleVerify}
                    className="flex-1 h-11 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-xl font-semibold"
                  >
                    {verifyLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      "Verify"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
